"use client";

import { ChevronRightIcon, LinkIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@ubud-ui/react-ui-kit";
import Link from "next/link";

export type RegistrationOptionCardProps = {
  title: string;
  href?: string;
  description?: string;
  icon?: React.ReactNode;
  footer?: { description?: string; info?: string };
  disabled?: boolean;
  disabledText?: string;
  externalLink?: string;
  externalHref?: string;
};

export function RegistrationOptionCard({
  title,
  href,
  description,
  icon,
  footer,
  disabled,
  disabledText = "Akan Datang",
  externalLink,
  externalHref,
}: RegistrationOptionCardProps) {
  const Card = (
    <>
      <div
        className={cn(
          "bg-white rounded-xl shadow-sm py-6 px-5 flex items-center gap-4 relative border border-gray-200",
          disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:border-primary-500",
        )}
      >
        {disabledText && disabled && (
          <div className="absolute top-0 right-0 text-right py-1 px-2 text-xs rounded-bl-lg rounded-tr-xl bg-yellow-500 text-yellow-800 font-medium">
            Akan datang
          </div>
        )}
        <div className="flex-1 flex items-start gap-4">
          {icon ? icon : null}
          <div className="flex-1 space-y-2">
            <h4 className="font-bold text-base">{title}</h4>
            {description && <p className="text-sm font-normal text-gray-500">{description}</p>}
            {externalLink && (
              <div className="flex items-start gap-1.5 text-xs font-medium text-[#0B6FC2]">
                <LinkIcon className="size-4 min-w-4 mt-0.5" />
                {externalLink}
              </div>
            )}
          </div>
        </div>
        <ChevronRightIcon className="size-4 min-w-4" />
      </div>
      {footer && footer.description && (
        <div className="bg-gray-50 pt-6 pb-2 px-5 text-xs font-medium -mt-4 flex items-center rounded-b-xl">
          <span className="text-gray-500 inline-block">{footer.description}</span>
          {footer.info && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger className="ml-1">
                <QuestionMarkCircleIcon className="size-4 min-w-4" />
              </TooltipTrigger>
              <TooltipContent className="bg-gray-800 text-white text-sm font-normal">
                <div className="w-[320px]">{footer.info}</div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {disabled ? (
        <div className="relative">{Card}</div>
      ) : href ? (
        <Link href={href} className="relative">
          {Card}
        </Link>
      ) : externalHref ? (
        <a href={externalHref} target="_blank">
          {Card}
        </a>
      ) : (
        Card
      )}
    </>
  );
}
